import React from 'react';
import { Link } from 'react-router-dom';

const ServiceHighlights = () => {
  // Service data with titles, descriptions, and icons
  const services = [
    {
      id: 'ai',
      title: 'AI & Digital Transformation',
      description: 'Advanced AI solutions, custom GPTs, and digital innovation strategies.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      id: 'strategic',
      title: 'Strategic Planning & Execution',
      description: 'Tailored roadmaps, governance frameworks, and strategic advisory.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z" />
        </svg>
      ),
    },
    {
      id: 'product',
      title: 'Product Management & Marketing',
      description: 'Comprehensive product strategy, UX refinement, and go-to-market execution.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
        </svg>
      ),
    },
    {
      id: 'program',
      title: 'Program & Project Delivery',
      description: 'End-to-end program oversight, agile implementation, and risk management.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      id: 'cloud',
      title: 'Cloud & Technical Consulting',
      description: 'Cloud migration, multi-cloud strategy, and infrastructure optimization.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" viewBox="0 0 20 20" fill="currentColor">
          <path d="M5.5 16a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 16h-8z" />
        </svg>
      ),
    },
  ];

  return (
    <section className="py-20 bg-dark relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-40 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -left-20 w-80 h-80 bg-accent/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-light mb-4">
            Our Core <span className="text-accent">Services</span>
          </h2>
          <p className="text-light/80 text-lg max-w-3xl mx-auto">
            Specialized expertise to drive transformation and deliver measurable value for organizations of all sizes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* First row: 3 cards */}
          {services.slice(0, 3).map((service, index) => (
            <div 
              key={index}
              className="bg-dark/50 border border-light/10 rounded-xl p-6 flex flex-col 
                transition-all duration-300 ease-in-out
                hover:border-accent/30 hover:scale-[1.02] hover:shadow-lg hover:shadow-accent/10 
                hover:bg-dark/70 transform-gpu group"
            >
              <div className="text-accent mb-5 transition-all duration-300 
                group-hover:text-accent/80 group-hover:scale-110 transform-gpu">
                {service.icon}
              </div>
              
              <h3 className="text-xl font-semibold text-light mb-3 transition-colors duration-300
                group-hover:text-accent/90">
                {service.title}
              </h3>
              
              <p className="text-light/70 mb-5 flex-grow transition-colors duration-300
                group-hover:text-light/90">
                {service.description}
              </p>
              
              <Link 
                to={`/services?tab=${service.id}`} 
                className="inline-flex items-center text-accent transition-all duration-300 mt-auto
                  group-hover:text-accent font-medium relative group-hover:pl-1"
              >
                <span className="font-medium relative after:absolute after:bottom-0 after:left-0 after:h-0.5 
                  after:bg-accent after:w-0 group-hover:after:w-full after:transition-all after:duration-300">
                  Learn More
                </span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </Link>
            </div>
          ))}
          
          {/* Second row: 2 cards centered */}
          <div className="md:col-span-3 flex justify-center mt-8 gap-8">
            {services.slice(3, 5).map((service, index) => (
              <div 
                key={index + 3}
                className="bg-dark/50 border border-light/10 rounded-xl p-6 flex flex-col 
                  transition-all duration-300 ease-in-out
                  hover:border-accent/30 hover:scale-[1.02] hover:shadow-lg hover:shadow-accent/10 
                  hover:bg-dark/70 transform-gpu group w-full md:w-1/3"
              >
                <div className="text-accent mb-5 transition-all duration-300 
                  group-hover:text-accent/80 group-hover:scale-110 transform-gpu">
                  {service.icon}
                </div>
                
                <h3 className="text-xl font-semibold text-light mb-3 transition-colors duration-300
                  group-hover:text-accent/90">
                  {service.title}
                </h3>
                
                <p className="text-light/70 mb-5 flex-grow transition-colors duration-300
                  group-hover:text-light/90">
                  {service.description}
                </p>
                
                <Link 
                  to={`/services?tab=${service.id}`} 
                  className="inline-flex items-center text-accent transition-all duration-300 mt-auto
                    group-hover:text-accent font-medium relative group-hover:pl-1"
                >
                  <span className="font-medium relative after:absolute after:bottom-0 after:left-0 after:h-0.5 
                    after:bg-accent after:w-0 group-hover:after:w-full after:transition-all after:duration-300">
                    Learn More
                  </span>
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-5 w-5 ml-2 transition-transform duration-300 group-hover:translate-x-1" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                  >
                    <path 
                      fillRule="evenodd" 
                      d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" 
                      clipRule="evenodd" 
                    />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceHighlights;